/**
 * Settings → Betriebsparameter (HOF-708): the farm's own settings — the values Hofsync works with for
 * this farm. **Every farm setting has its editor here** (`farmParameters.ts`): the calculation (the
 * Fester Kulturanteil), the labour app (what a worker can report, who may sign in), the data sources
 * (which systems, which one leads, who wins a contested value, tolerances, matching) and the country.
 *
 * One read — `GET /api/v1/farm/settings` — lists what this seat may read, each with whether it may
 * write it and the registry's vocabularies; each editor saves its own setting with the version it
 * read (`PUT /api/v1/farm/settings/{namespace}/{key}`). The backend decides; `writable` only decides
 * whether an editor offers its save.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SettingsPane } from '@hofsync/design-system';
import api from '../../services/api';
import type { SettingsPaneChrome } from './paneChrome';
import { SETTINGS_LIST_URL, settingId, type FarmSettingsList } from './farmParameters';
import { EDITORS, SETTINGS_LIST_KEY } from './parameterEditors';
import './FarmParametersPane.css';

const GROUPS: { id: string; labelKey: string; fallback: string; settings: string[] }[] = [
    {
        id: 'calculation',
        labelKey: 'settings.parameters.groups.calculation',
        fallback: 'Kalkulation',
        settings: ['labour_cost.fixed_shares'],
    },
    {
        id: 'labour',
        labelKey: 'settings.parameters.groups.labour',
        fallback: 'Arbeitszeit-App',
        settings: ['labour.catalogue', 'labour.access'],
    },
    {
        id: 'sources',
        labelKey: 'settings.parameters.groups.sources',
        fallback: 'Datenquellen',
        settings: ['sources.declared', 'sources.roles', 'sources.precedence', 'sources.reconciliation', 'sources.matching'],
    },
    { id: 'farm', labelKey: 'settings.parameters.groups.farm', fallback: 'Betrieb', settings: ['farm.country'] },
];

export default function FarmParametersPane({ backLink, focusOnMount }: SettingsPaneChrome) {
    const { t } = useTranslation();
    const list = useQuery<FarmSettingsList>({
        queryKey: SETTINGS_LIST_KEY,
        queryFn: async () => (await api.get(SETTINGS_LIST_URL, { withCredentials: true })).data,
    });
    const byId = new Map((list.data?.items ?? []).map((item) => [settingId(item), item]));

    return (
        <SettingsPane
            title={t('settings.parameters.title', 'Betriebsparameter')}
            description={t('settings.parameters.subtitle', 'Die Werte, mit denen Hofsync für deinen Betrieb rechnet.')}
            backLink={backLink}
            focusOnMount={focusOnMount}
        >
            {list.isLoading && <p role="status">{t('settings.parameters.loading', 'Wird geladen …')}</p>}
            {list.isError && (
                <p role="alert">{t('settings.parameters.loadFailed', 'Die Betriebsparameter konnten nicht geladen werden.')}</p>
            )}
            {list.data &&
                GROUPS.map((group) => {
                    const items = group.settings.flatMap((id) => {
                        const item = byId.get(id);
                        return item ? [item] : [];
                    });
                    if (items.length === 0) return null;
                    return (
                        <section key={group.id} className="farm-parameters__group" aria-labelledby={`parameters-${group.id}`}>
                            <h2 id={`parameters-${group.id}`} className="farm-parameters__group-title">
                                {t(group.labelKey, group.fallback)}
                            </h2>
                            {items.map((item) => {
                                const Editor = EDITORS[settingId(item)];
                                return Editor ? <Editor key={settingId(item)} item={item} options={list.data.options} /> : null;
                            })}
                        </section>
                    );
                })}
        </SettingsPane>
    );
}
